import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, Megaphone, Copy, Play, Pause, Eye, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import AdSlot from "@/components/AdSlot";

const DEFAULT_LABEL = "Contenido patrocinado";
const EMPTY = {
  name: "", advertiser: "", format: "banner",
  image_url: "", logo_url: "", destination_url: "",
  headline: "", description: "", label: DEFAULT_LABEL,
  placements: [], category_slug: "", logos: [],
  strip_scrolling: false, strip_scroll_speed: 30,
  start_date: "", end_date: "",
  priority: 50, frequency_cap: 0, device: "both",
  status: "draft", notes: "",
};

const FORMAT_LABELS = {
  micro_sponsor: "Micro sponsor",
  banner: "Banner compacto",
  native_card: "Card nativa patrocinada",
  sidebar: "Sidebar",
  sponsored_section: "Sección patrocinada",
  logo_strip: "Tira de logos",
  branded_content: "Contenido de marca (artículo)",
  billboard: "Billboard (premium)",
  takeover: "Takeover (premium)",
};
const SLOT_LABELS = {
  home_header_strip: "Home — tira bajo header",
  home_between_sections: "Home — entre secciones",
  home_native_card: "Home — card nativa",
  home_sidebar: "Home — sidebar",
  home_footer_strip: "Home — tira al pie",
  article_header: "Artículo — header",
  article_after_intro: "Artículo — tras intro",
  article_mid: "Artículo — medio",
  article_footer: "Artículo — pie",
  article_sidebar: "Artículo — sidebar",
  category_header: "Categoría — encabezado",
  category_native_card: "Categoría — card nativa",
  category_banner: "Categoría — banner",
};

export default function AdminAdsTab() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);

  const reload = async () => {
    const [a, m] = await Promise.all([
      api.get("/admin/ads/campaigns"),
      api.get("/admin/ads/meta"),
    ]);
    setItems(a.data || []);
    setMeta(m.data);
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...draft,
        priority: Number(draft.priority) || 50,
        frequency_cap: Number(draft.frequency_cap) || 0,
        strip_scroll_speed: Number(draft.strip_scroll_speed) || 30,
      };
      if (draft.id) await api.put(`/admin/ads/campaigns/${draft.id}`, payload);
      else await api.post("/admin/ads/campaigns", payload);
      toast.success("Campaña guardada");
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const setStatus = async (c, s) => {
    await api.post(`/admin/ads/campaigns/${c.id}/status`, { status: s });
    reload();
  };
  const duplicate = async (c) => {
    await api.post(`/admin/ads/campaigns/${c.id}/duplicate`);
    toast.success("Duplicada");
    reload();
  };
  const del = async (c) => {
    if (!window.confirm(`¿Eliminar la campaña "${c.name}"?`)) return;
    await api.delete(`/admin/ads/campaigns/${c.id}`);
    reload();
  };
  const openStats = async (c) => {
    const r = await api.get(`/admin/ads/campaigns/${c.id}/stats`);
    setStats({ campaign: c, ...r.data });
  };

  const availableFormats = (meta?.formats || []).filter((f) => meta?.premium_enabled || !meta?.premium_formats?.includes(f));

  const togglePlacement = (slot) => {
    setDraft((d) => {
      const has = d.placements.includes(slot);
      return { ...d, placements: has ? d.placements.filter((x) => x !== slot) : [...d.placements, slot] };
    });
  };
  const addLogo = () => setDraft((d) => ({ ...d, logos: [...(d.logos || []), { logo_url: "", destination_url: "", name: "" }] }));
  const updateLogo = (i, k, v) => setDraft((d) => ({ ...d, logos: d.logos.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const removeLogo = (i) => setDraft((d) => ({ ...d, logos: d.logos.filter((_, idx) => idx !== i) }));

  const statusBadge = (s) => {
    const colors = { active: "bg-green-500 text-white", paused: "bg-amber-500 text-black", draft: "bg-gray-300 text-gray-800" };
    return <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${colors[s] || colors.draft}`}>{s}</span>;
  };

  return (
    <div data-testid="admin-ads-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><Megaphone size={26} /> Publicidad</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Campañas, formatos y placements. Sin pop-ups, sin autoplay con audio, sin takeovers por defecto.</p>
        </div>
        <button onClick={() => setDraft({ ...EMPTY })} className="btn-accent" data-testid="admin-ad-new">
          <Plus size={16} /> Nueva campaña
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin campañas. Creá la primera.</p>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} className="border rounded-lg p-3 flex items-start gap-3 flex-wrap" style={{ borderColor: "var(--border)", opacity: c.status === "paused" ? 0.7 : 1 }} data-testid={`admin-ad-row-${c.id}`}>
              {(c.image_url || c.logo_url) && (
                <img src={c.image_url || c.logo_url} alt="" className="w-20 h-14 object-cover rounded flex-shrink-0 bg-gray-100" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {statusBadge(c.status)}
                  <span className="font-bold">{c.name}</span>
                  <span className="text-[11px] opacity-60">por {c.advertiser}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-pink-100 text-pink-700 rounded font-bold">{FORMAT_LABELS[c.format] || c.format}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs opacity-70 flex-wrap">
                  <span><strong>{c.impressions || 0}</strong> impresiones</span>
                  <span><strong>{c.clicks || 0}</strong> clicks</span>
                  <span>CTR <strong>{c.ctr || 0}%</strong></span>
                  <span>{(c.placements || []).length} placement(s)</span>
                  {c.start_date && <span>desde {String(c.start_date).slice(0, 10)}</span>}
                  {c.end_date && <span>hasta {String(c.end_date).slice(0, 10)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {c.status !== "active" && (
                  <button onClick={() => setStatus(c, "active")} className="text-xs px-2 py-1.5 rounded border font-bold flex items-center gap-1" style={{ borderColor: "var(--border)" }} title="Activar" data-testid={`admin-ad-activate-${c.id}`}><Play size={12} /></button>
                )}
                {c.status === "active" && (
                  <button onClick={() => setStatus(c, "paused")} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} title="Pausar" data-testid={`admin-ad-pause-${c.id}`}><Pause size={12} /></button>
                )}
                <button onClick={() => setPreview(c)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} title="Preview"><Eye size={12} /></button>
                <button onClick={() => openStats(c)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} title="Stats"><BarChart3 size={12} /></button>
                <button onClick={() => duplicate(c)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} title="Duplicar"><Copy size={12} /></button>
                <button onClick={() => setDraft(c)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} title="Editar" data-testid={`admin-ad-edit-${c.id}`}><Edit size={12} /></button>
                <button onClick={() => del(c)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} title="Eliminar" data-testid={`admin-ad-delete-${c.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Draft editor */}
      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDraft(null)}>
          <div className="bg-[var(--bg)] max-w-3xl w-full rounded-lg p-5 space-y-3 border max-h-[92vh] overflow-y-auto" style={{ borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between sticky top-0 bg-[var(--bg)] pb-2">
              <h3 className="font-headline uppercase text-xl">{draft.id ? "Editar" : "Nueva"} campaña</h3>
              <button onClick={() => setDraft(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="text" placeholder="Nombre interno" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-ad-name" />
              <input type="text" placeholder="Anunciante / Marca" value={draft.advertiser} onChange={(e) => setDraft({ ...draft, advertiser: e.target.value })} className="px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-ad-advertiser" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-70 block mb-1">Formato</label>
              <select value={draft.format} onChange={(e) => setDraft({ ...draft, format: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-ad-format">
                {availableFormats.map((f) => <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>)}
              </select>
            </div>

            {draft.format !== "logo_strip" && draft.format !== "branded_content" && (
              <>
                <input type="url" placeholder="URL imagen principal (JPG/PNG/WebP/GIF)" value={draft.image_url || ""} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} />
                <input type="url" placeholder="URL logo (opcional, para micro sponsor o sección)" value={draft.logo_url || ""} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
              </>
            )}
            {draft.format === "native_card" && (
              <>
                <input type="text" placeholder="Titular" value={draft.headline || ""} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} />
                <textarea placeholder="Descripción corta" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm min-h-[60px]" style={{ borderColor: "var(--border)" }} />
              </>
            )}
            {draft.format === "logo_strip" && (
              <div className="border rounded p-3" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase">Logos de la tira</span>
                  <button onClick={addLogo} className="text-xs px-2 py-1 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid="admin-ad-logo-add"><Plus size={11} /> Logo</button>
                </div>
                {(draft.logos || []).map((l, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-7 gap-2 mb-2">
                    <input type="text" placeholder="Nombre" value={l.name || ""} onChange={(e) => updateLogo(i, "name", e.target.value)} className="md:col-span-2 px-2 py-1 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
                    <input type="url" placeholder="URL logo" value={l.logo_url || ""} onChange={(e) => updateLogo(i, "logo_url", e.target.value)} className="md:col-span-2 px-2 py-1 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
                    <input type="url" placeholder="URL destino" value={l.destination_url || ""} onChange={(e) => updateLogo(i, "destination_url", e.target.value)} className="md:col-span-2 px-2 py-1 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
                    <button onClick={() => removeLogo(i)} className="text-xs px-2 py-1 rounded border font-bold" style={{ borderColor: "var(--border)" }}><Trash2 size={11} /></button>
                  </div>
                ))}
                <label className="flex items-center gap-2 text-xs mt-2">
                  <input type="checkbox" checked={!!draft.strip_scrolling} onChange={(e) => setDraft({ ...draft, strip_scrolling: e.target.checked })} />
                  Movimiento horizontal automático
                </label>
                {draft.strip_scrolling && (
                  <input type="number" min="10" max="120" value={draft.strip_scroll_speed} onChange={(e) => setDraft({ ...draft, strip_scroll_speed: e.target.value })} className="mt-2 px-2 py-1 border rounded bg-transparent text-xs w-28" style={{ borderColor: "var(--border)" }} placeholder="Velocidad (s)" />
                )}
              </div>
            )}

            <input type="url" placeholder="URL destino al hacer click" value={draft.destination_url || ""} onChange={(e) => setDraft({ ...draft, destination_url: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} />
            <input type="text" placeholder={`Etiqueta (default: "${DEFAULT_LABEL}")`} value={draft.label || ""} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />

            {draft.format === "sponsored_section" && (
              <input type="text" placeholder="Slug de categoría (ej: tecnologia)" value={draft.category_slug || ""} onChange={(e) => setDraft({ ...draft, category_slug: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            )}

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold opacity-70 block mb-1">Placements</label>
              <div className="flex flex-wrap gap-1">
                {(meta?.slots || []).map((slot) => {
                  const on = draft.placements.includes(slot);
                  return (
                    <button key={slot} onClick={() => togglePlacement(slot)} className="text-[11px] px-2 py-1 rounded border font-bold" style={{ borderColor: on ? "var(--accent)" : "var(--border)", background: on ? "var(--accent)" : "transparent", color: on ? "#fff" : "var(--text)" }} data-testid={`admin-ad-slot-${slot}`}>
                      {SLOT_LABELS[slot] || slot}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] uppercase font-bold opacity-70 block">Desde</label>
                <input type="datetime-local" value={draft.start_date || ""} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className="w-full px-2 py-1 border rounded bg-transparent text-xs" style={{ borderColor: "var(--border)" }} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold opacity-70 block">Hasta</label>
                <input type="datetime-local" value={draft.end_date || ""} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className="w-full px-2 py-1 border rounded bg-transparent text-xs" style={{ borderColor: "var(--border)" }} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold opacity-70 block">Prioridad (0-100)</label>
                <input type="number" min="0" max="100" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} className="w-full px-2 py-1 border rounded bg-transparent text-xs" style={{ borderColor: "var(--border)" }} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold opacity-70 block">Dispositivo</label>
                <select value={draft.device} onChange={(e) => setDraft({ ...draft, device: e.target.value })} className="w-full px-2 py-1 border rounded bg-transparent text-xs" style={{ borderColor: "var(--border)" }}>
                  <option value="both">Ambos</option>
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                </select>
              </div>
            </div>

            <textarea placeholder="Notas internas" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm min-h-[50px]" style={{ borderColor: "var(--border)" }} />

            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase font-bold opacity-70">Estado</label>
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="px-2 py-1 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="admin-ad-status">
                <option value="draft">Borrador</option>
                <option value="active">Activa</option>
                <option value="paused">Pausada</option>
              </select>
            </div>

            <button onClick={save} className="btn-accent w-full" data-testid="admin-ad-save"><Save size={14} /> Guardar</button>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPreview(null)}>
          <div className="bg-[var(--bg)] max-w-xl w-full rounded-lg p-5 border" style={{ borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-headline uppercase text-xl">Preview — {preview.name}</h3>
              <button onClick={() => setPreview(null)}><X size={18} /></button>
            </div>
            <div className="border rounded p-6 bg-[var(--surface)]" data-testid="admin-ad-preview">
              <AdSlotPreview campaign={preview} />
            </div>
            <p className="text-[10px] opacity-60 mt-3">Las impresiones del preview no se cuentan.</p>
          </div>
        </div>
      )}

      {/* Stats modal */}
      {stats && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setStats(null)}>
          <div className="bg-[var(--bg)] max-w-2xl w-full rounded-lg p-5 border" style={{ borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-headline uppercase text-xl">Estadísticas — {stats.campaign.name}</h3>
              <button onClick={() => setStats(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="border rounded p-3" style={{ borderColor: "var(--border)" }}><div className="text-[10px] uppercase opacity-70">Impresiones</div><div className="font-headline text-2xl">{stats.impressions}</div></div>
              <div className="border rounded p-3" style={{ borderColor: "var(--border)" }}><div className="text-[10px] uppercase opacity-70">Clicks</div><div className="font-headline text-2xl">{stats.clicks}</div></div>
              <div className="border rounded p-3" style={{ borderColor: "var(--border)" }}><div className="text-[10px] uppercase opacity-70">CTR</div><div className="font-headline text-2xl">{stats.ctr}%</div></div>
            </div>
            {stats.daily?.length > 0 && (
              <table className="w-full text-xs">
                <thead><tr className="text-left opacity-70"><th>Fecha</th><th className="text-right">Impr.</th><th className="text-right">Clicks</th><th className="text-right">CTR</th></tr></thead>
                <tbody>
                  {stats.daily.map((d) => (
                    <tr key={d.date} className="border-t" style={{ borderColor: "var(--border)" }}><td className="py-1">{d.date}</td><td className="text-right">{d.impressions}</td><td className="text-right">{d.clicks}</td><td className="text-right">{d.ctr}%</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Mini renderer for admin preview (without fetching from backend)
function AdSlotPreview({ campaign }) {
  switch (campaign.format) {
    case "micro_sponsor":
      return <div className="inline-flex items-center gap-2"><span className="text-[9px] uppercase opacity-70">{campaign.label}</span>{campaign.logo_url && <img src={campaign.logo_url} alt="" style={{ maxHeight: 20 }} />}<strong className="text-xs">{campaign.advertiser}</strong></div>;
    case "banner":
      return campaign.image_url ? <img src={campaign.image_url} alt="" style={{ maxWidth: "100%", margin: "0 auto", display: "block" }} /> : null;
    case "native_card":
      return (
        <article className="border rounded max-w-sm mx-auto" style={{ borderColor: "var(--border)" }}>
          <div className="text-[10px] px-3 pt-2 text-pink-500 font-bold uppercase">{campaign.label}</div>
          {campaign.image_url && <img src={campaign.image_url} alt="" className="w-full aspect-video object-cover" />}
          <div className="p-3">
            <h4 className="font-headline uppercase text-lg">{campaign.headline || campaign.name}</h4>
            {campaign.description && <p className="text-xs opacity-70 mt-1">{campaign.description}</p>}
            <div className="text-[10px] uppercase font-bold mt-2 opacity-60">{campaign.advertiser}</div>
          </div>
        </article>
      );
    case "logo_strip":
      return (
        <div className="flex items-center justify-center gap-6 flex-wrap">
          <span className="text-[10px] uppercase opacity-70 w-full text-center">{campaign.label}</span>
          {(campaign.logos || []).map((l, i) => l.logo_url && <img key={i} src={l.logo_url} alt={l.name || ""} style={{ maxHeight: 36 }} />)}
        </div>
      );
    default:
      return <pre className="text-xs opacity-70">{JSON.stringify(campaign, null, 2).slice(0, 400)}</pre>;
  }
}
